import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { MODAL_ORDER_LIST } from './modalOrder';

interface IModalQueueContextType {
  unregisterModal: (id: string) => void;
  showModal: (id: string) => void;
  hideModal: (id: string) => void;
  isModalActive: (id: string) => boolean;
  activeModal: string | null;
}

const ModalQueueContext = createContext<IModalQueueContextType | null>(null);

export const useModalQueue = () => {
  const context = useContext(ModalQueueContext);
  if (!context) {
    throw new Error('useModalQueue must be used within a ModalQueueProvider');
  }
  return context;
};

interface IModalQueueProviderProps {
  children: React.ReactNode;
}

const sortModalsByPriority = (queue: string[]): string[] => {
  return [...queue].sort((a, b) => {
    const indexA = MODAL_ORDER_LIST.indexOf(a as any);
    const indexB = MODAL_ORDER_LIST.indexOf(b as any);

    // If both modals are in the predefined list, sort by that order
    if (indexA !== -1 && indexB !== -1) {
      // Lower index should come first in the array
      return indexA - indexB;
    }

    // If only one modal is in the list, prioritize it
    if (indexA !== -1) return -1; // a comes first
    if (indexB !== -1) return 1; // b comes first

    // If neither is in the list, keep their original order
    return 0;
  });
};

export const ModalQueueProvider = ({ children }: IModalQueueProviderProps) => {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [queue, setQueue] = useState<string[]>([]);

  const unregisterModal = useCallback((id: string) => {
    setQueue((prev) => prev.filter((modalId) => modalId !== id));
  }, []);

  const showModal = useCallback((id: string) => {
    // Add the modal to the queue if it's not already there
    setQueue((prev) => {
      if (prev.includes(id)) return prev;

      // Add the modal to the queue
      const newQueue = [...prev, id];

      // Sort the queue based on the predefined modal order
      return sortModalsByPriority(newQueue);
    });
  }, []);

  const hideModal = useCallback((id: string) => {
    // Simply remove the modal from the queue
    setQueue((prev) => {
      return prev.filter((modalId) => modalId !== id);
    });
  }, []);

  const isModalActive = useCallback(
    (id: string) => {
      return activeModal === id;
    },
    [activeModal],
  );

  // Process the queue when it changes - and ONLY the queue
  // This avoids an infinite loop by not depending on activeModal
  useEffect(() => {
    if (queue.length > 0) {
      // Sort the queue to make sure we're selecting the highest priority modal
      const sortedQueue = sortModalsByPriority(queue);

      // Always set the active modal to the highest priority one in the queue
      const highestPriorityModal = sortedQueue[0];
      setActiveModal(highestPriorityModal);
    } else if (queue.length === 0 && activeModal !== null) {
      // If the queue is empty, clear the active modal
      setActiveModal(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue]);

  return (
    <ModalQueueContext.Provider
      value={{
        unregisterModal,
        showModal,
        hideModal,
        isModalActive,
        activeModal,
      }}
    >
      {children}
    </ModalQueueContext.Provider>
  );
};
